// react
import React from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// react-pdf
import { Document, Page, pdfjs } from "react-pdf";

// react-xml-viewer
import XMLViewer from "react-xml-viewer";

// @mui
import { useMediaQuery, Dialog, Box, IconButton, Typography, Tooltip } from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon, CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// A ---------------------------------------------------------------------- M

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const BaseDialog = ({ open, onClose, children, paperSx }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="md"
    sx={{
      "& .MuiPaper-root": {
        borderRadius: 2,
        ...paperSx,
      },
    }}
  >
    {children}
  </Dialog>
);

const CloseButton = ({ onClose, floating = false, title }) => (
  <Tooltip title={title} placement="left">
    <IconButton
      onClick={onClose}
      color="error"
      sx={
        floating
          ? {
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              backgroundColor: "rgba(0,0,0,0.5)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }
          : { alignSelf: "flex-end" }
      }
    >
      <CloseOutlinedIcon />
    </IconButton>
  </Tooltip>
);

const FileViewerDialog = ({ fileType, fileUrl, xmlContent, textContent, currentPage, numPages, onPageChange, open, onClose }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const handlePdfLoad = ({ numPages }) => {
    onPageChange(1, numPages);
  };

  const handleDocumentLoadSuccess = ({ numPages }) => {
    onPageChange(1, numPages);
  };

  /* ======================= PDF ======================= */
  if (fileType === "application/pdf") {
    return (
      <BaseDialog open={open} onClose={onClose} paperSx={{ p: 2 }}>
        <CloseButton onClose={onClose} title={t("close")} />

        <Document file={fileUrl} onLoadSuccess={handlePdfLoad}>
          <Page pageNumber={currentPage} width={isMobile ? 320 : 600} renderAnnotationLayer={false} renderTextLayer={false} />
        </Document>

        <Box display="flex" justifyContent="center" alignItems="center" mt={1}>
          <IconButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            <KeyboardArrowLeftOutlinedIcon />
          </IconButton>
          <Typography>{`${currentPage} / ${numPages}`}</Typography>
          <IconButton onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === numPages}>
            <KeyboardArrowRightOutlinedIcon />
          </IconButton>
        </Box>
      </BaseDialog>
    );
  }

  /* ======================= XML ======================= */
  if (fileType === "text/xml" || fileType === "application/xml") {
    return (
      <BaseDialog open={open} onClose={onClose} paperSx={{ p: 2 }}>
        <CloseButton onClose={onClose} title={t("close")} />
        <Box sx={{ p: 2, maxHeight: "70vh", overflow: "auto" }}>
          <XMLViewer xml={xmlContent} />
        </Box>
      </BaseDialog>
    );
  }

  /* ======================= TEXT / CSV ======================= */
  if (fileType === "text/plain" || fileType === "text/csv") {
    return (
      <BaseDialog open={open} onClose={onClose} paperSx={{ p: 2 }}>
        <CloseButton onClose={onClose} title={t("close")} />
        <Box sx={{ p: 2, maxHeight: "70vh", overflow: "auto" }}>
          <Typography component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
            {textContent}
          </Typography>
        </Box>
      </BaseDialog>
    );
  }

  /* ======================= JSON ======================= */
  if (fileType === "application/json") {
    return (
      <BaseDialog open={open} onClose={onClose} paperSx={{ p: 2 }}>
        <CloseButton onClose={onClose} title={t("close")} />
        <Box sx={{ p: 2, maxHeight: "70vh", overflow: "auto" }}>
          <pre>{JSON.stringify(textContent, null, 2)}</pre>
        </Box>
      </BaseDialog>
    );
  }

  /* ======================= IMAGE ======================= */
  if (fileType.startsWith("image/")) {
    return (
      <BaseDialog open={open} onClose={onClose} paperSx={{ p: 0, backgroundColor: "#000", overflow: "hidden" }}>
        <CloseButton onClose={onClose} floating title={t("close")} />
        <Box display="flex" justifyContent="center" alignItems="center">
          <img src={fileUrl} alt="Preview" style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />
        </Box>
      </BaseDialog>
    );
  }

  /* ======================= VIDEO ======================= */
  if (fileType.startsWith("video/")) {
    return (
      <BaseDialog open={open} onClose={onClose} paperSx={{ p: 0, backgroundColor: "#000", overflow: "hidden" }}>
        <CloseButton onClose={onClose} floating title={t("close")} />
        <Box display="flex" justifyContent="center" alignItems="center">
          <video src={fileUrl} controls autoPlay style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}>
            {t("video_not_supported")}
          </video>
        </Box>
      </BaseDialog>
    );
  }

  return null;
};

export default FileViewerDialog;
